import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'chart.js/auto';
import { createPortal } from 'react-dom';
import { useMediaQuery } from 'react-responsive';
import mediaQueries from 'common/utils/mediaQueries';
import { truncateString } from '../../helper';
import { datasetColors } from '../../enum';

function DoughnutPercentageChart(props) {
  const {
    labels,
    data,
    images,
    height,
    radius,
    cutout,
    maxLabels,
    extraDatasets = [],
    layout = 'horizontal',
    growLegend,
    Tooltip,
  } = props;
  const isMobile = useMediaQuery({ ...mediaQueries.mobile });
  let maxLegendLength = 45;
  if (isMobile) {
    maxLegendLength = 20;
  }

  const chartRef = useRef();
  const canvasRef = useRef(null);
  const [tooltipModel, setTooltipModel] = useState();
  const dataPoint = tooltipModel?.dataPoints[0];
  const image = dataPoint && images?.[dataPoint.dataIndex];
  const labelsCount = maxLabels ?? labels.length;
  const containerHeight = height ?? labelsCount * 25 + 150;

  useEffect(() => {
    if (!canvasRef.current) return;

    chartRef.current?.destroy();
    chartRef.current = new Chart(canvasRef.current, {
      type: 'doughnut',
      data: {
        labels: labels.map((label) => truncateString(label, 280)),
        datasets: [
          {
            data,
            label: 'Percentage',
            backgroundColor: datasetColors,
            animation: false,
            radius,
            cutout,
          },
          ...extraDatasets,
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 0 },
        plugins: {
          legend: {
            display: true,
            position: layout === 'horizontal' ? 'right' : 'top',
            onHover: (evt, legendItem) => {
              const activeElement = {
                datasetIndex: 0,
                index: legendItem.index,
              };
              chartRef.current.setActiveElements([activeElement]); // to also show thick border
              chartRef.current.tooltip.setActiveElements([activeElement]);
              chartRef.current.update();
            },
            labels: {
              boxWidth: 14,
              boxHeight: 14,
              color: '#222',
              generateLabels(chart) {
                const original = Chart.overrides.doughnut.plugins.legend.labels.generateLabels;
                const labelsOriginal = original.call(this, chart);
                return labelsOriginal.slice(0, labelsCount).map((label) => ({
                  ...label,
                  text: `${truncateString(label.text, maxLegendLength)} (${data[label.index]}%)`,
                }));
              },
            },
          },
          tooltip: Tooltip
            ? { enabled: false, external: (context) => setTooltipModel({ ...context.tooltip }) }
            : {},
        },
      },
      plugins: [
        {
          id: 'legendFit',
          beforeInit: (chart) => {
            const originalFit = chart.legend.fit;
            // eslint-disable-next-line no-param-reassign
            chart.legend.fit = function newFit() {
              originalFit.call(this);
              if (layout === 'horizontal') {
                if (growLegend && radius) {
                  // eslint-disable-next-line react/no-this-in-sfc
                  this.width = chart.width - radius * 2 - 20;
                } else {
                  // eslint-disable-next-line react/no-this-in-sfc
                  this.width += 20;
                }
              } else {
                // eslint-disable-next-line react/no-this-in-sfc
                this.height += 10;
              }
            };
          },
        },
      ],
    });
  }, [data]);

  return (
    <>
      <div style={{ height: `${containerHeight}px` }}>
        <canvas className="engage-report-question-charts__canvas" ref={canvasRef} />
      </div>
      {Tooltip
        ? createPortal(
            <Tooltip tooltipModel={tooltipModel} canvasRef={canvasRef} image={image} />,
            document.body
          )
        : null}
    </>
  );
}

DoughnutPercentageChart.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  data: PropTypes.arrayOf(PropTypes.number).isRequired,
  images: PropTypes.arrayOf(PropTypes.string),
  extraDatasets: PropTypes.arrayOf(PropTypes.shape({})),
  Tooltip: PropTypes.func,
  maxLabels: PropTypes.number,
  height: PropTypes.number,
  radius: PropTypes.number,
  cutout: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  layout: PropTypes.oneOf(['horizontal', 'vertical']),
  growLegend: PropTypes.bool,
};

DoughnutPercentageChart.defaultProps = {
  images: [],
  extraDatasets: undefined,
  Tooltip: null,
  maxLabels: null,
  height: null,
  radius: 100,
  cutout: undefined,
  layout: 'horizontal',
  growLegend: undefined,
};

export default DoughnutPercentageChart;
